import { useRef } from "react"
import { NodeResizeControl, OnResizeEnd, ShouldResize, useReactFlow } from "@xyflow/react"

import { AppNode, GroupNode } from "@features/builder/graph/types"
import {
  expandedGroupMinHeight,
  expandedGroupMinWidth,
  groupPadding,
} from "@features/builder/helpers/nodes"
import { nodesGroupMapper } from "@features/builder/helpers/nodesGroupMapper"
import { getNodeSize } from "@features/builder/graph/nodes/NodeGroupResizable/getNodeSize"
import css from "@features/builder/graph/nodes/NodeGroupResizable/NodeGroupResizable.module.css"
import { OnResizeStart, ResizeControlVariant } from "@xyflow/system"
import { getRect } from "@features/builder/graph/nodes/NodeGroupResizable/getRect"

export const NodeResizeBottom = ({ node }: { node: GroupNode }) => {
  const { getNodes, getInternalNode, updateNode } = useReactFlow<AppNode>()
  const constrainedSizeRef = useRef<{ width: number; height: number } | null>(null)

  const handleResizeStart: OnResizeStart = (_event, _p) => {
    const mapper = nodesGroupMapper(getNodes())
    const parents = mapper.childToParentGroupsIdsMapDeep.get(node.id) ?? []
    for (const parentId of parents) {
      updateNode(parentId, { expandParent: true })
    }
    updateNode(node.id, { expandParent: true })
  }

  const handleResizeEnd: OnResizeEnd = (_event, { width, height }) => {
    const mapper = nodesGroupMapper(getNodes())
    const parents = mapper.childToParentGroupsIdsMapDeep.get(node.id) ?? []

    const finalWidth = constrainedSizeRef.current?.width ?? width
    const finalHeight = constrainedSizeRef.current?.height ?? height
    constrainedSizeRef.current = null

    for (const parentId of parents) {
      const parentNode = mapper.groupsMap.get(parentId)

      const deepthFromResized = parents.length + 1 - Number(parentNode?.data.nestingLevel)
      const { height } = getNodeSize(parentNode, deepthFromResized)

      updateNode(parentId, {
        expandParent: false,
        style: { width: parentNode?.style?.width, height },
      })
    }
    updateNode(node.id, {
      expandParent: false,
      style: { width: finalWidth, height: finalHeight },
    })
  }

  const handleShouldResize: ShouldResize = (_event, resizedNode) => {
    let constrainedHeight = resizedNode.height

    const internalResizedNode = getInternalNode(node.id)
    const resizedNodeAbs = getRect.resizedNodeAbs(internalResizedNode)
    const groupBoundriesAbs = getRect.groupAbs(resizedNodeAbs, resizedNode)

    const mapper = nodesGroupMapper(getNodes())
    const subGroups = mapper.parentToChildGroupsIdsMap.get(node.id) ?? []
    const subNodes = mapper.groupToNodesIdsMap.get(node.id) ?? []

    // SUB-NODES and SUB-GROUPS
    for (const childNodeId of [...subNodes, ...subGroups]) {
      const internalChildNode = getInternalNode(childNodeId)
      const childNode = mapper.allMap.get(childNodeId)
      const child = getRect.child(internalChildNode, childNode)
      const childBoundriesAbs = getRect.childAbs(child)

      const dontResize = groupBoundriesAbs.bottom <= childBoundriesAbs.bottom
      if (dontResize) {
        const minHeightForChild = child.y - resizedNodeAbs.y + child.height + groupPadding
        constrainedHeight = Math.max(constrainedHeight, minHeightForChild)
      }
    }

    constrainedSizeRef.current = {
      width: resizedNode.width,
      height: constrainedHeight,
    }

    return constrainedHeight === resizedNode.height
  }

  return (
    <NodeResizeControl
      position="bottom"
      className={css.resizeLine}
      variant={ResizeControlVariant.Line}
      minWidth={expandedGroupMinWidth}
      minHeight={expandedGroupMinHeight}
      shouldResize={handleShouldResize}
      autoScale={true}
      onResizeStart={handleResizeStart}
      onResizeEnd={handleResizeEnd}
    />
  )
}
